import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import { UX2, UX, constants as coreConstants } from '@wsb/guac-widget-core';
import { omit, isEmpty, merge } from 'lodash';
import dataAids from '../../constants/dataAids';
import { isDeviceMobile } from '../../utils/viewDevice';
import { EMBED } from '../../../../../constants/videoTypes';
import { NONE } from '../../../../../constants/overlayTypes';
const { PUBLISH, DISPLAY, EDIT } = coreConstants.renderModes;

class HeaderVideoBackground extends Component {
  constructor(props) {
    super(props);
    const { videoBackground, videoType, videoStyle } = props;
    this.video = React.createRef();
    this.embedWrapper = React.createRef();
    const isUploadedVimeo = videoBackground && videoBackground.vimeoId;
    this.state = {
      hasEmbed: videoType === EMBED || isUploadedVimeo,
      dynamicHeight: videoStyle.dynamicHeight || false,
      videoHeight: 'unset',
      videoWidth: 'unset',
      isWide: true,
      scale: '1'
    };
  }

  static formatUrl(url) {
    return isEmpty(url) || url.indexOf('https') === 0 ? url : `https:${url}`;
  }

  handleMatchMedia = (data, override = {}) => {
    const { viewDevice, renderMode } = Object.assign({}, this.props, override);
    const isMobile = renderMode === PUBLISH ? data.size === 'xs' : isDeviceMobile(viewDevice);
    if (isMobile !== this.state.isMobile) {
      this.setState({ isMobile });
    }
  };

  getVideoDimension = () => {
    const { isMobile, dynamicHeight } = this.state;
    const { renderMode } = this.props;
    if (!dynamicHeight) return { videoWidth: 'unset', videoHeight: 'unset' };

    const fullWidth = {
      videoWidth: '100%',
      videoHeight: 'unset'
    };
    const videoNode = this.video.current;

    if (videoNode && videoNode.closest) {
      const section = videoNode.closest(`[data-aid="${dataAids.HEADER_SECTION}"]`);
      let padding = renderMode === DISPLAY ? 200 : 50;
      if (section && videoNode) {
        const videoRatio = videoNode.clientWidth / videoNode.clientHeight;
        const heightDiff = section.clientHeight - videoNode.clientHeight;
        const widthIncrease = videoRatio * heightDiff;
        if (widthIncrease + videoNode.clientWidth < section.clientWidth && !isMobile) {
          return fullWidth;
        }
        if (widthIncrease + videoNode.clientWidth < section.clientWidth) {
          padding += 50;
        }
        return {
          videoWidth: 'unset',
          videoHeight: section.clientHeight + padding
        };
      }
    }
    return { videoWidth: 'unset', videoHeight: 'unset' };
  };

  setEmbedScale = () => {
    const { videoType, videoEmbed, videoBackground } = this.props;
    const embed = videoType === EMBED ? videoEmbed : videoBackground;
    const { width, height } = embed;
    const { isWide: prevIsWide, scale: prevScale } = this.state;
    let videoAspectRatio, wrapperAspectRatio;
    let currentIsWide = prevIsWide;
    let currentScale = prevScale;

    if (width && height) {
      // In the case where we just swapped from video to embed, we should use the existing video wrapper's width / height
      // since the embedWrapper won't exist yet.
      const wrapperNode = this.embedWrapper.current || this.video.current;

      if (wrapperNode) {
        videoAspectRatio = (width / height).toFixed(2);
        wrapperAspectRatio = (wrapperNode.clientWidth / wrapperNode.clientHeight).toFixed(2);

        if (wrapperAspectRatio < videoAspectRatio) {
          currentIsWide = false;
          currentScale = videoAspectRatio / wrapperAspectRatio;
        } else {
          currentIsWide = true;
          currentScale = '1';
        }
      }
    }

    if (currentIsWide !== prevIsWide || (!currentIsWide && currentScale !== prevScale)) {
      this.setState({ isWide: currentIsWide, scale: currentScale });
    }
  };

  updateVideoDimension = e => {
    const { renderMode } = this.props;
    const { videoWidth, videoHeight } = this.state;
    const eventType = (e && e.type) || '';
    if (eventType === 'resize' && renderMode === DISPLAY) return;
    const videoDimension = this.getVideoDimension();
    if (videoDimension.videoWidth !== videoWidth || videoDimension.videoHeight !== videoHeight) {
      this.setState(videoDimension);
    }
  };

  componentDidMount() {
    const { hasEmbed } = this.state;

    if (hasEmbed) {
      window.addEventListener('resize', this.setEmbedScale);
      this.setEmbedScale();
    } else {
      if (this.state.dynamicHeight) {
        window.addEventListener('resize', this.updateVideoDimension);
      }
      this.updateVideoDimension();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateVideoDimension);
    window.removeEventListener('resize', this.setEmbedScale);
  }

  componentDidUpdate(prevProps) {
    const { viewDevice, videoType, videoBackground, tagline, tagline2 } = this.props;
    const isUploadedVimeo = videoBackground && videoBackground.vimeoId;
    const hasEmbed = videoType === EMBED || isUploadedVimeo;

    if (hasEmbed !== this.state.hasEmbed) {
      this.setState({ hasEmbed });
    }

    if (hasEmbed) {
      this.setEmbedScale();
    }
    if (viewDevice !== prevProps.viewDevice) {
      this.handleMatchMedia({}, { viewDevice });
    }
    if (!hasEmbed && (tagline !== prevProps.tagline || tagline2 !== prevProps.tagline2)) {
      this.updateVideoDimension();
    }
  }

  /**
   * Try to find the header section and set the background image to the header
   * section's dimensions.
   * @returns {Object} object with styles
   */
  getBackgroundImageStyles() {
    const { videoPoster, renderMode } = this.props;
    const { dynamicHeight } = this.state;
    const formatPosterUrl = (url, w, h) => {
      return `${url}/:/rs=w:${w},h:${h},cg:true,m/cr=w:${w},h:${h},a:cc`;
    };
    if (renderMode === PUBLISH && typeof document !== 'undefined' && dynamicHeight) {
      const section = document.querySelector(`[data-aid="${dataAids.HEADER_SECTION}"]`);
      if (!section) return {};
      const posterUrl = formatPosterUrl(videoPoster, section.clientWidth, section.clientHeight);
      return { backgroundImage: `url("${posterUrl.replace(/"/g, '\\"')}")` };
    }
    return {};
  }

  /**
   * If the dynamicHeight is turned on, get the resized dimensions for the video
   * on render. dynamicHeight is defined by layouts. For example dynamicHeight is off
   * for layout 7.
   * @returns {Object} return object with video dimensions
   */
  getDynamicHeightStyles() {
    const { renderMode } = this.props;
    const { dynamicHeight, videoHeight, videoWidth } = this.state;
    const videoStyle = {};
    if (dynamicHeight) {
      if (renderMode === DISPLAY) {
        const dimensions = this.getVideoDimension();
        videoStyle.width = dimensions.videoWidth;
        videoStyle.height = dimensions.videoHeight;
      } else {
        videoStyle.width = videoWidth;
        videoStyle.height = videoHeight;
      }
    }
    return videoStyle;
  }

  generateStyles() {
    const { videoStyle, videoBgStyle } = this.props;
    const { isMobile, hasEmbed } = this.state;
    let finalVideoBgStyle, finalVideoStyle;

    if (isMobile) {
      finalVideoBgStyle = videoBgStyle.mobile || videoBgStyle;
      finalVideoStyle = videoStyle.mobile || videoStyle;
    } else {
      finalVideoBgStyle = omit(videoBgStyle, ['mobile']);
      finalVideoStyle = omit(videoStyle, ['mobile', 'dynamicHeight']);
    }

    if (hasEmbed) {
      return {
        finalVideoBgStyle: merge(
          {
            position: 'relative',
            width: '100%',
            overflow: 'hidden',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          },
          finalVideoBgStyle
        ),
        finalVideoStyle: {
          ...finalVideoStyle,
          ...(isMobile ? { paddingBottom: 0 } : {}) /* Override 16:9 aspect ratio */
        }
      };
    }

    return {
      finalVideoBgStyle: Object.assign({}, finalVideoBgStyle, this.getBackgroundImageStyles()),
      finalVideoStyle: Object.assign({}, finalVideoStyle, this.getDynamicHeightStyles())
    };
  }

  generateEmbedStyles({
    finalVideoStyle,
    embedProps,
    isDisplay,
    isInitMobile,
    isVideoInset,
    renderMode
  }) {
    const { scale } = this.state;
    const { thumbnail, width: embedWidth, height: embedHeight } = embedProps;
    const isVerticalVideo = embedHeight > embedWidth;

    const embed = {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      transform: `scale(${scale})`
    };
    const embedBackground = {
      'background': `url(${thumbnail}) center/cover`,
      'width': '100%',
      'height': '100%',
      '@sm': {
        filter: 'blur(10px)'
      }
    };
    const embedBgWrapper = {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      overflow: 'hidden'
    };
    const embedWrapper = {
      position: 'relative',
      paddingTop:
        embedHeight && embedWidth && embedWidth > embedHeight
          ? `${(100 * embedHeight) / embedWidth}%`
          : `56.25%`,
      pointerEvents: 'none',
      width: '100%'
    };
    const insetStyles = {};
    const displayStyles = {};

    if (isVideoInset) {
      // If the video has a hardcoded height, scale the width according to the aspect ratio:
      const { height: videoStyleHeight, minHeight: videoStyleMinHeight } = finalVideoStyle;
      let finalVideoStyleHeight =
        videoStyleHeight &&
        (Number(videoStyleHeight) || videoStyleHeight.includes('px')) &&
        parseFloat(videoStyleHeight);
      if (videoStyleMinHeight && parseFloat(videoStyleMinHeight) > finalVideoStyleHeight) {
        finalVideoStyleHeight = parseFloat(videoStyleMinHeight);
      }
      if (finalVideoStyleHeight && !isInitMobile) {
        insetStyles.width = (finalVideoStyleHeight * embedWidth) / embedHeight;
      }

      if (isInitMobile || isDisplay) {
        embedWrapper.paddingTop = 0;
      }

      embedWrapper.pointerEvents = renderMode === EDIT ? 'none' : 'auto';

      // Styles for the DISPLAY render mode in the theme gallery:
      if (isDisplay) {
        displayStyles.width = isVerticalVideo || finalVideoStyle.width === 'auto' ? 'auto' : '100%';
        displayStyles.height = '100%';
        displayStyles.minWidth = 'none';
        displayStyles.minHeight = 'none';
        displayStyles.position = 'relative';
        embedWrapper.paddingBottom = 0;
        embedWrapper.display = 'flex';
        embedWrapper.justifyContent = 'center';
        embedWrapper.alignItems = 'center';
      }
    }

    return {
      embed,
      embedWrapper: { ...embedWrapper, ...finalVideoStyle, ...insetStyles },
      embedBackground,
      embedBgWrapper,
      poster: isDisplay ? { ...finalVideoStyle, ...displayStyles } : finalVideoStyle
    };
  }

  renderEmbed(finalVideoStyle, embedProps) {
    const { renderMode, isVideoInset, videoType, showMobileVideo } = this.props;
    const { isMobile } = this.state;
    const { thumbnail, vimeoId } = embedProps;
    const { alt } = (this.context && this.context.video) || {};
    const videoAlt = videoType !== EMBED ? this.props.videoAlt || alt : '';
    const isInitMobile = !showMobileVideo && (typeof isMobile === 'undefined' || isMobile);
    const isDisplay = renderMode === DISPLAY;
    const styles = this.generateEmbedStyles({
      finalVideoStyle,
      embedProps,
      isDisplay,
      isInitMobile,
      isVideoInset,
      renderMode
    });

    const poster = isVideoInset ? (
      // TODO: Replace img with UX2.Element.Image after thumbnails are stored in image steam
      // or after UX2.Element.Image can support external urls (UX2.Component.Background can handle external urls)
      <img
        src={ thumbnail }
        style={ styles.poster }
        alt={ videoAlt }
        data-aid={ dataAids.HEADER_VIDEO_EMBED_INSET_POSTER }
      />
    ) : (
      <UX2.Component.Background backgroundImage={ thumbnail } style={ styles.poster } overlay={ NONE } />
    );

    return (
      <Fragment>
        { !isVideoInset && (
          <div style={ styles.embedBgWrapper }>
            <div style={ styles.embedBackground } />
          </div>
        ) }
        <div
          style={ styles.embedWrapper }
          ref={ this.embedWrapper }
          data-aid={ dataAids.HEADER_VIDEO_EMBED_WRAPPER }
        >
          { isInitMobile || isDisplay ? (
            poster
          ) : (
            <Fragment>
              { vimeoId && (
                <iframe
                  style={ styles.embed }
                  src={ `https://player.vimeo.com/video/${vimeoId}?autoplay=1&loop=1&autopause=0&muted=1&title=0&byline=0&portrait=0&controls=0` }
                  frameBorder='0'
                  allow='autoplay; fullscreen'
                  webkitallowfullscreen
                  mozallowfullscreen
                  allowFullScreen
                  title={ videoAlt }
                  data-aid={ dataAids.HEADER_VIDEO_EMBED }
                />
              ) }
            </Fragment>
          ) }
        </div>
      </Fragment>
    );
  }

  renderVideo(finalVideoStyle) {
    const { videoPoster, videoBackground, renderMode, showMobileVideo } = this.props;
    const { isMobile } = this.state;
    const isInitMobile = !showMobileVideo && (typeof isMobile === 'undefined' || isMobile);
    const { alt: videoAlt } = (this.context && this.context.video) || {};
    const posterSrc = this.constructor.formatUrl(videoPoster);
    const formattedSrc =
      typeof videoBackground === 'string' ? this.constructor.formatUrl(videoBackground) : '';

    return (
      <video
        title={ this.props.videoAlt || videoAlt }
        data-aid={ dataAids.HEADER_VIDEO }
        ref={ this.video }
        style={ finalVideoStyle }
        src={ isInitMobile ? '' : formattedSrc }
        type='video/mp4'
        poster={ posterSrc }
        loop={ true }
        muted={ true }
        autoPlay={ renderMode !== DISPLAY }
        playsInline={ true }
      />
    );
  }

  render() {
    const { dataRoute, videoType, videoBackground, videoEmbed } = this.props;
    const { finalVideoBgStyle, finalVideoStyle } = this.generateStyles();
    const { hasEmbed } = this.state;

    return (
      <UX2.Element.Wrapper.Video style={ finalVideoBgStyle } data-field-id={ dataRoute }>
        <UX.MatchMedia onChange={ this.handleMatchMedia } />
        { hasEmbed
          ? this.renderEmbed(finalVideoStyle, videoType === EMBED ? videoEmbed : videoBackground)
          : this.renderVideo(finalVideoStyle) }
      </UX2.Element.Wrapper.Video>
    );
  }
}

HeaderVideoBackground.propTypes = {
  videoStyle: PropTypes.object,
  videoAlt: PropTypes.string,
  videoBgStyle: PropTypes.object,
  videoBackground: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
  videoPoster: PropTypes.string.isRequired,
  videoEmbed: PropTypes.object,
  videoType: PropTypes.string,
  renderMode: PropTypes.string.isRequired,
  viewDevice: PropTypes.string.isRequired,
  tagline: PropTypes.string,
  tagline2: PropTypes.string,
  dataRoute: PropTypes.string,
  isVideoInset: PropTypes.bool,
  showMobileVideo: PropTypes.bool
};

HeaderVideoBackground.defaultProps = {
  videoStyle: {},
  videoBgStyle: {},
  videoBackground: '',
  videoPoster: '',
  renderMode: '',
  viewDevice: '',
  tagline: '',
  tagline2: '',
  videoEmbed: {},
  showMobileVideo: false
};

HeaderVideoBackground.contextTypes = {
  video: PropTypes.object
};

export default HeaderVideoBackground;
